import Autocomplete from "@/components/panel/Autocomplete";
import usePaginatedList from "@/hooks/usePaginatedList";

/**
 * Valor de `productId` quando o item foi digitado à mão em vez de escolhido no
 * catálogo. Existe porque o `Autocomplete` só mostra o rótulo quando há valor.
 */
export const FREE_TEXT = "__fora_do_catalogo__";

/**
 * Escolha de produto (e variação) com busca no servidor.
 *
 * Entrega o produto e a variação inteiros, não só os ids: quem usa quase sempre
 * precisa do preço, do saldo ou do rótulo logo em seguida.
 *
 * Produto de variação única já sai escolhido — obrigar a abrir um select com uma
 * opção só ("Único") era clique perdido em toda movimentação de estoque.
 */
export default function ProductPicker({
  productId,
  variantId,
  onChange,
  selectedLabel,
  selectedVariantLabel,
  placeholder = "Buscar produto...",
  showVariant = true,
  // Aluguel de equipamento e peça sob-medida nem sempre estão cadastrados: com
  // isto a lista oferece "usar o que foi digitado", num campo só, em vez de um
  // combo de busca ao lado de uma caixa de texto solta.
  allowFreeText = false,
  // `/admin/products` é restrito ao admin; tela de balcão/vendedor deve passar
  // `/products` (catálogo), senão a busca volta 403 e parece "nada encontrado".
  path = "/admin/products",
  // Filtros que vão para o SERVIDOR junto da busca. A locação manda
  // `{ rentable: true }` para não oferecer o catálogo de venda num contrato de
  // aluguel — filtrar no cliente só esconderia o que já veio na página.
  filters = {},
  testId,
  width = "w-full",
}) {
  const list = usePaginatedList(path, filters, { perPage: 50 });

  const product = list.items.find((p) => p.id === productId) || null;
  const variants = product?.variants || [];
  const typed = (list.search || "").trim();

  const options = list.items.map((p) => ({ value: p.id, label: p.name }));
  if (allowFreeText && typed.length >= 2 && !list.items.some((p) => p.name.toLowerCase() === typed.toLowerCase())) {
    options.unshift({
      value: FREE_TEXT,
      label: `Usar "${typed}" (fora do catálogo)`,
      displayLabel: selectedLabel || typed,
    });
  }

  const pickProduct = (id) => {
    if (id === FREE_TEXT) { onChange({ product: null, variant: null, freeText: typed }); return; }
    const p = list.items.find((x) => x.id === id) || null;
    onChange({ product: p, variant: p?.variants?.length === 1 ? p.variants[0] : null });
  };

  const pickVariant = (id) => {
    onChange({ product, variant: variants.find((v) => v.id === id) || null });
  };

  return (
    <div className="space-y-1.5">
      <Autocomplete
        value={productId || ""}
        onChange={pickProduct}
        options={options}
        query={list.search}
        onQueryChange={list.setSearch}
        selectedLabel={selectedLabel}
        placeholder={placeholder}
        emptyLabel="— Nenhum produto"
        testId={testId}
        width={width}
      />

      {/* Some com produto de variação única: nesse caso já foi escolhida acima. */}
      {showVariant && variants.length > 1 && (
        <Autocomplete
          value={variantId || ""}
          onChange={pickVariant}
          options={variants.map((v) => ({
            value: v.id,
            label: `${v.label} (${v.stock} em estoque)`,
          }))}
          selectedLabel={selectedVariantLabel}
          placeholder="Escolha a variação"
          emptyLabel="— Nenhuma variação"
          testId={testId ? `${testId}-variant` : undefined}
          width={width}
        />
      )}
    </div>
  );
}
